import { useState, useEffect } from 'react';
import { ArticlesService } from '../services/articles.service';
import { CategoryService } from '../services/category.service';

/**
 * Hook personnalisé pour gérer les articles
 */
export const useArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchArticles = async (options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await ArticlesService.getAll(options);
      setArticles(result.items);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchArticlesByCategory = async (categoryId, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      console.log(`Hook: Récupération des articles pour la catégorie ${categoryId}`);
      
      const result = await ArticlesService.getByCategory(categoryId, options);
      
      console.log(`Hook: ${result.items.length} articles récupérés pour la catégorie ${categoryId}`);
      setArticles(result.items);
      return result;
    } catch (err) {
      // Ignorer les erreurs d'annulation automatique
      if (err.name === 'AbortError' || err.message?.includes('aborted') || err.message?.includes('autocancelled')) {
        console.log(`Hook: Requête annulée pour la catégorie ${categoryId}`);
        // Retourner un résultat vide au lieu de lancer une erreur
        return { items: [], totalItems: 0, page: 1, perPage: 0, totalPages: 0 };
      }
      
      setError(err.message);
      console.error('Erreur lors du chargement des articles par catégorie:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchArticlesByMultipleCategories = async (categoryIds, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      console.log(`Hook: Récupération des articles pour les catégories ${categoryIds.join(', ')}`);
      
      const result = await ArticlesService.getByMultipleCategories(categoryIds, options);
      
      console.log(`Hook: ${result.items.length} articles récupérés pour les catégories multiples`);
      setArticles(result.items);
      return result;
    } catch (err) {
      if (err.name === 'AbortError' || err.message?.includes('aborted') || err.message?.includes('autocancelled')) {
        console.log(`Hook: Requête annulée pour les catégories multiples`);
        return { items: [], totalItems: 0, page: 1, perPage: 0, totalPages: 0 };
      }
      
      setError(err.message);
      console.error('Erreur lors du chargement des articles par catégories multiples:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const searchArticles = async (query, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await ArticlesService.search(query, options);
      setArticles(result.items);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    articles,
    loading,
    error,
    fetchArticles,
    fetchArticlesByCategory,
    fetchArticlesByMultipleCategories,
    searchArticles,
    setArticles
  };
};

/**
 * Hook pour récupérer un article spécifique par ID ou slug
 */
export const useArticle = (identifier) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!identifier) {
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Détecter si c'est un ID (format PocketBase) ou un slug
        const isId = identifier.length === 15 && /^[a-zA-Z0-9]+$/.test(identifier);
        
        let articleData;
        if (isId) {
          articleData = await ArticlesService.getById(identifier);
        } else {
          articleData = await ArticlesService.getBySlug(identifier);
        }
        
        // Ne mettre à jour l'état que si la requête n'a pas été annulée
        if (!isCancelled) {
          setArticle(articleData);
        }
      } catch (err) {
        // Ignorer les erreurs d'annulation
        if (!isCancelled && !err.message?.includes('autocancelled')) {
          setError(err.message);
          console.error('Erreur lors du chargement de l\'article:', err);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchArticle();

    // Cleanup function pour marquer la requête comme annulée
    return () => {
      isCancelled = true;
    };
  }, [identifier]);

  return { article, loading, error, setArticle };
};

// Cache simple pour les catégories
let categoriesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook pour gérer les catégories avec cache
 */
export const useCategories = () => {
  const [categories, setCategories] = useState(categoriesCache || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Vérifier le cache d'abord
      const now = Date.now();
      if (categoriesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
        console.log('Hook: Utilisation du cache pour les catégories');
        setCategories(categoriesCache);
        setLoading(false);
        return categoriesCache;
      }
      
      console.log('Hook: Récupération des catégories depuis PocketBase...');
      
      const result = await CategoryService.getAllCategories();
      
      console.log(`Hook: ${result.length} catégories récupérées`);
      
      // Mettre en cache
      categoriesCache = result;
      cacheTimestamp = now;
      
      setCategories(result);
      return result;
    } catch (err) {
      // Ignorer les erreurs d'annulation automatique
      if (err.name === 'AbortError' || err.message?.includes('aborted')) {
        console.log('Hook: Requête annulée pour les catégories');
        return categoriesCache || [];
      }
      
      setError(err.message);
      console.error('Erreur lors du chargement des catégories:', err);
      
      // Retourner le cache si disponible, sinon tableau vide
      const fallback = categoriesCache || [];
      setCategories(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoriesWithCount = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await CategoryService.getCategoriesWithCount();
      setCategories(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    categories,
    loading,
    error,
    fetchCategories,
    fetchCategoriesWithCount,
    setCategories
  };
};
